import React, { useMemo, useState } from 'react'
import { Button } from 'antd'
import { Link, useHistory } from 'react-router-dom'
import styled from 'styled-components/macro'
import GrowthEngineHumanDemoRow from 'src/components/growthEngine/GrowthEngineHumanDemoRow'
import { planDisplayName } from 'src/constants/planDisplay'

const DISMISS_KEY_PREFIX = 'oms-lite-home-discovery-dismissed:'

const FEATURE_CHIPS = [
  {
    id: 'menuBuilder',
    label: 'Menu costing',
    blurb: 'Know what dishes cost and understand your gross profit.',
    path: venueId => `/v/${venueId}/menu-builder`,
  },
  {
    id: 'receipting',
    label: 'Invoice checks',
    blurb: 'Spend less time checking supplier invoices.',
    path: venueId => `/v/${venueId}/purchasing/receipting`,
  },
  {
    id: 'stock',
    label: 'Stock',
    blurb: 'See what’s on hand, spot waste, improve purchasing.',
    path: venueId => `/v/${venueId}/stockTakes`,
  },
  {
    id: 'foodSafety',
    label: 'Food Safety',
    blurb: 'Keep temperature checks and records in one place.',
    path: venueId => `/v/${venueId}/food-safety`,
  },
]

/**
 * Compact Lite-only Home section: platform discovery without crowding ordering.
 * Internal plan key remains 'Free'.
 */
const LitePlatformDiscovery = ({ venueId, currentPlan }) => {
  const history = useHistory()
  const dismissKey = `${DISMISS_KEY_PREFIX}${venueId || 'unknown'}`
  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(dismissKey) === '1'
    } catch {
      return false
    }
  })

  const isLite = useMemo(() => {
    const plan = String(currentPlan || 'Free').trim()
    return plan === 'Free' || plan.toLowerCase() === 'free'
  }, [currentPlan])

  if (!isLite || !venueId || dismissed) {
    return null
  }

  const plansPath = `/v/${venueId}/subscriptions/oms-plans`
  const liteName = planDisplayName('Free')

  const dismiss = () => {
    try {
      window.localStorage.setItem(dismissKey, '1')
    } catch {
      /* ignore */
    }
    setDismissed(true)
  }

  return (
    <Section data-testid='lite-platform-discovery'>
      <HeaderRow>
        <div>
          <Title>You’re using {liteName}</Title>
          <Intro>
            You’re already ordering with OMS. Discover what else we can help you
            do.
          </Intro>
        </div>
        <DismissButton
          type='button'
          onClick={dismiss}
          data-testid='lite-platform-discovery-dismiss'
          aria-label='Dismiss Lite discovery'
        >
          Hide
        </DismissButton>
      </HeaderRow>
      <ChipGrid>
        {FEATURE_CHIPS.map(chip => (
          <Chip
            key={chip.id}
            as={Link}
            to={chip.path(venueId)}
            data-testid={`lite-discovery-chip-${chip.id}`}
          >
            <ChipLabel>{chip.label}</ChipLabel>
            <ChipBlurb>{chip.blurb}</ChipBlurb>
            <ChipAction>Explore</ChipAction>
          </Chip>
        ))}
      </ChipGrid>
      <Actions>
        <Button
          data-testid='lite-discovery-see-lite'
          onClick={() => history.push(plansPath)}
        >
          See what’s in Lite
        </Button>
        <Button
          type='primary'
          data-testid='lite-discovery-view-plans'
          onClick={() => history.push(plansPath)}
        >
          View paid plans
        </Button>
      </Actions>
      <DemoBlock>
        <DemoLine>
          Want to see what OMS could do for your business? Book a free,
          no-obligation demo with our team.
        </DemoLine>
        <GrowthEngineHumanDemoRow compact />
      </DemoBlock>
    </Section>
  )
}

export default LitePlatformDiscovery

const Section = styled.section`
  margin: 1rem 0 0.25rem;
  padding: 1rem 1.1rem 1.1rem;
  border: 1px solid ${({ theme }) => theme.colours?.lettuceGreen || '#b5d922'};
  border-radius: 12px;
  background: ${({ theme }) => theme.colours?.mayoWhite || '#fffef8'};
`

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`

const Title = styled.h2`
  margin: 0 0 0.25rem;
  font-size: 1.05rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colours?.bbqBlack || '#111'};
`

const Intro = styled.p`
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours?.oliveGrey || '#555'};
`

const DismissButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colours?.oliveGrey || '#666'};
  font-size: 0.85rem;
  cursor: pointer;
  text-decoration: underline;
  padding: 0.15rem 0.25rem;
  flex-shrink: 0;
`

const ChipGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(11rem, 1fr));
  gap: 0.6rem;
  margin-bottom: 0.85rem;
`

const Chip = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.7rem 0.75rem;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colours?.lettuceGreen || '#cfe89a'};
  background: #fff;
  text-decoration: none;
  color: inherit;
  &:hover {
    border-color: ${({ theme }) => theme.colours?.bbqBlack || '#111'};
  }
`

const ChipLabel = styled.span`
  font-weight: 700;
  font-size: 0.9rem;
`

const ChipBlurb = styled.span`
  font-size: 0.8rem;
  line-height: 1.35;
  color: ${({ theme }) => theme.colours?.oliveGrey || '#555'};
`

const ChipAction = styled.span`
  margin-top: 0.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: underline;
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`

const DemoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`

const DemoLine = styled.p`
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours?.oliveGrey || '#555'};
`
